import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { OperatorResponse } from 'src/apis/capacity-management/types';
import type { OptionalCapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { isServerError, useAxios } from 'src/hooks/use-axios';

type HookConfig = {
  capacityManagementRoot?: OptionalCapacityManagementRootRequest;
  operatorId: string;
};

type RequestOptions = {
  url: string;
};

export function useOperatorByTso({
  capacityManagementRoot,
  operatorId,
}: HookConfig) {
  const operatorLink = capacityManagementRoot?.response?.data._links?.operator;

  const operator = useAxios(
    (axios, baseConfig, { url }: RequestOptions) =>
      axios.request<OperatorResponse>({
        ...baseConfig,
        url,
      }),
    {
      neededOnPageLoad: true,
      onError(error) {
        if (isServerError(error, 403)) {
          // operator is not subscribed to cama
          return error;
        } else {
          throw error;
        }
      },
    }
  );

  useEffect(() => {
    if (!operatorLink || !capacityManagementRoot.response) return;

    const url = urlTemplate.parse(operatorLink.href).expand({ operatorId });

    operator.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [operatorId, capacityManagementRoot?.response]);

  // either carma itself or the operator link aren't rolled out on this stage
  // (or we're authenticated as an operator)
  // in any case, we aren't interested in the request
  if (!operatorLink) return;

  return operator;
}

export type OperatorRequest = ReturnType<typeof useOperatorByTso>;
